import { useState, useEffect, useRef, useMemo } from 'react';
import { fmt, Spinner, SlipPreviewModal } from './UI';
import PaymentSheet from './PaymentSheet';
import {
  getTransactionsForHub, addSubmission, addDirectSubmission,
  getSubmissionsForTransaction, uploadSlip, getDirectSubmissions,
  loginHubUser, hashPassword, getAllSubmissionsForHub, deleteSubmission,
} from '../lib/supabase';

// Use local date parts throughout — avoids UTC/IST timezone shift bugs
function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(dateStr, n) {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(y, mo - 1, d + n));
  const yy  = date.getUTCFullYear();
  const mm  = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd  = String(date.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function formatDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: '2-digit', timeZone: 'UTC',
  });
}

// Returns every date from (maxExcelDate + 1) up to and including today.
// If no excel data exists, returns just today.
function getMissingDates(transactions) {
  const dates = transactions
    .map(t => t.collection_date)
    .filter(Boolean)
    .sort();
  const maxDate = dates.length ? dates[dates.length - 1] : null;
  const start = maxDate ? addDays(maxDate, 1) : todayStr();
  const end   = todayStr();
  const result = [];
  for (let d = start; d <= end; d = addDays(d, 1)) {
    result.push(d);
  }
  return result;
}

const PAYMENT_MODES = [
  { key: 'cms',           label: '💵 Handover to CMS' },
  { key: 'neft_flipkart', label: '🏦 NEFT to Flipkart' },
  { key: 'neft_wheeley',  label: '🏦 NEFT to Wheeley' },
];

const MODE_LABEL = { cms: 'CMS', neft_flipkart: 'NEFT→FK', neft_wheeley: 'NEFT→WH' };

// ─── History tab helpers (module-level — no state deps) ───────────────────────
const HIST_MN = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function histSubMonth(s) {
  const baseDate = s.txn_date || s.submission_date;
  if (!baseDate) return null;
  const [y, m] = baseDate.split('-');
  return `${HIST_MN[parseInt(m, 10) - 1]}-${String(y).slice(2)}`;
}
function histSubKind(s) {
  // Strip optional [ADMIN] prefix then trim before checking kind tag
  const core = (s.remarks || '').trim().replace(/^\[ADMIN\]\s*/i, '').trim();
  if (core.startsWith('[LOSS]'))   return 'Loss';
  if (core.startsWith('[SHORT]'))  return 'Short';
  if (core.startsWith('[EXCESS]')) return 'Excess';
  return 'Full';
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [shake,    setShake]    = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setLoading(true);
    setError('');
    try {
      const hash = await hashPassword(password);
      const user = await loginHubUser(username, hash);
      if (user) {
        onLogin(user);
      } else {
        setError('Invalid username or password.');
        setShake(true);
        setTimeout(() => setShake(false), 500);
      }
    } catch (e) {
      setError('Login failed: ' + e.message);
    }
    setLoading(false);
  }

  const inputStyle = {
    background: '#0f1117', color: '#f3f4f6', border: '1px solid #374151',
    borderRadius: 8, width: '100%', padding: '11px 14px', fontSize: 14,
    fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0b0e14', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Mono', monospace", padding: 16 }}>
      <div style={{
        background: '#1a1f2e', border: '1px solid #2a2f3e', borderRadius: 20,
        width: '100%', maxWidth: 380, padding: 36,
        animation: shake ? 'shake .4s ease' : 'none',
      }}>
        <style>{`
          @keyframes shake {
            0%,100% { transform: translateX(0); }
            20%,60%  { transform: translateX(-8px); }
            40%,80%  { transform: translateX(8px); }
          }
        `}</style>

        {/* Logo + title */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ background: '#f59e0b', borderRadius: 12, width: 52, height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <span style={{ color: '#0f1117', fontWeight: 900, fontSize: 24 }}>W</span>
          </div>
          <div style={{ color: '#f59e0b', fontWeight: 700, fontSize: 15, letterSpacing: 3 }}>WHEELEY</div>
          <div style={{ color: '#6b7280', fontSize: 11, marginTop: 4 }}>HUB CASH SUBMISSION PORTAL</div>
          <div style={{ color: '#374151', fontSize: 11, marginTop: 12 }}>Sign in with your hub credentials</div>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 6 }}>USERNAME</label>
            <input
              type="text"
              placeholder="Enter username"
              value={username}
              onChange={e => { setUsername(e.target.value); setError(''); }}
              autoComplete="username"
              style={{ ...inputStyle, borderColor: error ? '#f87171' : '#374151' }}
            />
          </div>
          <div>
            <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 6 }}>PASSWORD</label>
            <input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              autoComplete="current-password"
              style={{ ...inputStyle, borderColor: error ? '#f87171' : '#374151' }}
            />
          </div>

          {error && (
            <div style={{ background: '#1c0f0f', border: '1px solid #f8717140', borderRadius: 8, padding: '10px 12px', color: '#f87171', fontSize: 12 }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={loading || !username.trim() || !password}
            style={{
              background: loading ? '#2d1f00' : '#f59e0b',
              color: loading ? '#6b7280' : '#0f1117',
              border: 'none', borderRadius: 8, padding: '13px',
              fontWeight: 900, fontSize: 12, letterSpacing: 2,
              cursor: loading ? 'wait' : 'pointer', fontFamily: 'inherit',
              marginTop: 4, opacity: (!username.trim() || !password) ? 0.5 : 1,
            }}>
            {loading ? 'SIGNING IN...' : '→ SIGN IN'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Submission Modal ──────────────────────────────────────────────────────────
function SubmissionModal({ txn, uploadId, hub, onClose, onSuccess, directDate, preloadedSubs }) {
  const isDirectSubmission = !!directDate;

  const [mode,           setMode]           = useState('cms');
  const [submissionKind, setSubmissionKind] = useState('full');
  const [form, setForm] = useState({
    amount:      '',
    ref:         '',
    date:        directDate || todayStr(),
    remarks:     '',
    submittedBy: '',
  });
  const [slipFile,    setSlipFile]    = useState(null);
  const [slipPreview, setSlipPreview] = useState('');
  const [previewSlip, setPreviewSlip] = useState(null);  // inline proof preview
  const [submitting,   setSubmitting]   = useState(false);
  const [showConfirm,  setShowConfirm]  = useState(false);
  // Use pre-loaded subs from parent (covers orphaned submissions with stale transaction IDs).
  // Fall back to fetching from DB only for direct submissions or if no preloaded subs provided.
  const [existingSubs, setExistingSubs] = useState(preloadedSubs || []);
  const [loadingSubs,  setLoadingSubs]  = useState(!isDirectSubmission && !preloadedSubs);
  const fileRef = useRef();

  useEffect(() => {
    // If preloaded subs were provided, use them directly — no fetch needed
    if (preloadedSubs) {
      setExistingSubs(preloadedSubs);
      setLoadingSubs(false);
      return;
    }
    if (isDirectSubmission) return;
    (async () => {
      setLoadingSubs(true);
      try {
        const subs = await getSubmissionsForTransaction(txn.id);
        setExistingSubs(subs);
      } catch (e) { console.error(e); }
      setLoadingSubs(false);
    })();
  }, [txn.id, isDirectSubmission]); // eslint-disable-line

  // Validate all fields, then show the confirmation panel
  function requestConfirm() {
    if (!form.date) return;
    // Loss/Utilized: only remarks required — no amount/ref/slip checks
    if (submissionKind === 'loss') {
      if (!form.remarks.trim()) {
        alert('Remarks are required for a Loss/Utilized declaration — explain what happened to the money.');
        return;
      }
      setShowConfirm(true);
      return;
    }
    // Full/Loss: amount is locked to effective_remaining in the UI — form.amount stays ''
    const isLockedKind = (submissionKind === 'full' || submissionKind === 'loss') && !isDirectSubmission;
    if (!isLockedKind && !form.amount) return;
    // Excess: the submitted amount MUST exceed the outstanding remaining balance for this row.
    // If it doesn't, the submission isn't actually "excess" — the hub should use Full or Short.
    if (submissionKind === 'excess' && !isDirectSubmission) {
      const remaining = Number(txn.effective_remaining ?? txn.remaining ?? 0);
      const entered   = parseFloat(form.amount) || 0;
      if (entered <= remaining) {
        alert(
          `Excess submissions must be MORE than the remaining balance (₹${remaining.toLocaleString('en-IN')}).\n\n` +
          `You entered ₹${entered.toLocaleString('en-IN')}. ` +
          (entered === remaining
            ? 'That matches exactly — please use the Full option instead.'
            : 'That is less than the remaining amount — please use the Short option instead.')
        );
        return;
      }
    }
    if (!form.ref.trim()) {
      alert(mode === 'cms' ? 'CMS Ref / Slip No is required.' : 'UTR / Ref Number is required.');
      return;
    }
    if (!slipFile) {
      alert('Please upload the deposit slip or screenshot. It is required for Full, Short and Excess submissions.');
      return;
    }
    if (submissionKind === 'short' && !form.remarks.trim()) {
      alert('Remarks are required for a short submission.');
      return;
    }
    if (submissionKind === 'excess' && !form.remarks.trim()) {
      alert('Remarks are required for an excess submission — mention which date\'s shortfall this covers.');
      return;
    }
    setShowConfirm(true);
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      let slipUrl = null;
      if (slipFile) slipUrl = await uploadSlip(slipFile, hub);

      const remarksStr = submissionKind === 'short'
        ? `[SHORT] ${form.remarks}`
        : submissionKind === 'excess'
          ? `[EXCESS] ${form.remarks}`
          : submissionKind === 'loss'
            ? `[LOSS] ${form.remarks}`
            : form.remarks;

      // Full/Loss amounts are locked to effective_remaining in the UI; form.amount is '' for those
      const isLockedKind = (submissionKind === 'full' || submissionKind === 'loss') && !isDirectSubmission;
      const resolvedAmount = isLockedKind
        ? Number(txn.effective_remaining ?? txn.remaining ?? 0)
        : parseFloat(form.amount) || 0;

      const entry = {
        type:        submissionKind === 'loss' ? 'cms' : mode,  // 'loss' not in DB enum; [LOSS] remarks prefix identifies it
        amount:      resolvedAmount,
        ref:         submissionKind === 'loss' ? '' : form.ref,
        date:        form.date,
        remarks:     remarksStr,
        submittedBy: form.submittedBy,
        slipUrl:     submissionKind === 'loss' ? null : slipUrl,
      };

      if (isDirectSubmission) {
        await addDirectSubmission(uploadId, hub, directDate, entry);
      } else {
        await addSubmission(uploadId, hub, txn.id, entry);
      }
      onSuccess();
    } catch (e) { alert('Submission failed: ' + e.message); }
    setSubmitting(false);
  }

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setSlipFile(file);
    const reader = new FileReader();
    reader.onload = ev => setSlipPreview(ev.target.result);
    reader.readAsDataURL(file);
  }

  const inputStyle = {
    background: '#0b0e14', color: '#f3f4f6', border: '1px solid #374151',
    borderRadius: 8, width: '100%', padding: '9px 12px', fontSize: 13,
    fontFamily: 'inherit', boxSizing: 'border-box',
  };

  // Always show the form — even when fully cleared, the hub may need to submit
  // an Excess payment (to cover a previous day's shortfall on this date).
  const canSubmit = true;
  const isCleared = !isDirectSubmission && Number(txn.effective_remaining ?? txn.remaining ?? 0) <= 0;

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000000cc', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: '#1a1f2e', border: '1px solid #2a2f3e', borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', fontFamily: "'DM Mono', monospace" }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #2a2f3e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: '#f59e0b', fontWeight: 700, fontSize: 13 }}>
              {isDirectSubmission ? 'COD DEPOSIT SUBMISSION' : 'SUBMIT PROOF'}
            </div>
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>
              {isDirectSubmission
                ? `${formatDate(directDate)} · ${hub}`
                : `${txn.collection_date || 'No date'} · Sr #${txn.sr_no || txn.id}`}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Amounts summary — only for regular txns */}
          {!isDirectSubmission && (() => {
            const effectiveRem  = txn.effective_remaining ?? txn.remaining ?? 0;
            const submittingAmt = submissionKind === 'zero' ? 0
              : submissionKind === 'full' ? effectiveRem
              : parseFloat(form.amount || 0);
            const shortAmt  = Math.max(0, effectiveRem - submittingAmt);
            const excessAmt = Math.max(0, submittingAmt - effectiveRem);
            const showPriorDep = txn.has_prior_deposit;
            const priorColor   = txn.prior_deposit_match ? '#34d399' : '#f87171';
            const fourthCol = submissionKind === 'excess'
              ? ['EXCESS AMT',  excessAmt > 0 ? fmt(excessAmt) : '–', '#3b82f6']
              : submissionKind === 'loss'
                ? ['WRITE-OFF',  fmt(effectiveRem), '#f97316']
                : ['SHORT AMT',  shortAmt > 0 ? fmt(shortAmt) : '₹0 (cleared)', shortAmt > 0 ? '#f87171' : '#34d399'];
            return (
              <>
                {isCleared && submissionKind !== 'excess' && submissionKind !== 'loss' && (
                  <div style={{ background: '#0c1c14', border: '1px solid #34d39940', borderRadius: 8, padding: '8px 12px', color: '#34d399', fontSize: 12 }}>
                    ✅ Row is already cleared — switch to <strong>💰 Excess</strong> if you're depositing extra to cover another date's shortfall.
                  </div>
                )}
                <div style={{ background: '#0f1117', borderRadius: 10, padding: 14, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                  {[
                    ['TOTAL COLLECTION', fmt(txn.amount_to_collect || txn.diff), '#60a5fa'],
                    showPriorDep
                      ? ['PRIOR DEP', fmt(txn.direct_amt), priorColor]
                      : ['SUBMITTED', fmt(txn.effective_submitted ?? txn.total_submitted), '#34d399'],
                    ['REMAINING', fmt(effectiveRem), effectiveRem > 0 ? '#f87171' : '#34d399'],
                    fourthCol,
                  ].map(([l, v, c]) => (
                    <div key={l}>
                      <div style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1 }}>{l}</div>
                      <div style={{ color: c, fontSize: 14, fontWeight: 700, marginTop: 3 }}>{v}</div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}

          {/* Reconciliation breakdown — only for regular txns */}
          {!isDirectSubmission && (txn.transaction_id || txn.amount_to_collect) && (
            <div style={{ background: '#0f1117', borderRadius: 10, padding: 12 }}>
              <div style={{ color: '#374151', fontSize: 9, letterSpacing: 2, marginBottom: 8 }}>RECONCILIATION DETAILS</div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 10 }}>
                <div>
                  <div style={{ color: '#374151', fontSize: 9, letterSpacing: 1 }}>TXN ID</div>
                  <div style={{ color: '#6b7280', fontSize: 10, fontFamily: 'monospace', marginTop: 2, wordBreak: 'break-all' }}>
                    {txn.transaction_id || '—'}
                  </div>
                </div>
                {[
                  ['TO COLLECT',   fmt(txn.amount_to_collect), '#60a5fa'],
                  ['COD CREDIT 1', txn.cod_credit_1 ? fmt(txn.cod_credit_1) : '—', '#34d399'],
                  ['COD CREDIT 2', txn.cod_credit_2 ? fmt(txn.cod_credit_2) : '—', '#34d399'],
                  ['DIFF',         fmt(txn.diff), txn.diff > 0 ? '#f87171' : '#34d399'],
                ].map(([label, value, color]) => (
                  <div key={label}>
                    <div style={{ color: '#374151', fontSize: 9, letterSpacing: 1 }}>{label}</div>
                    <div style={{ color, fontSize: 12, fontWeight: 700, marginTop: 2 }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Previous submissions — only for regular txns */}
          {!isDirectSubmission && (
            loadingSubs ? <Spinner /> : existingSubs.length > 0 && (
              <div>
                <div style={{ color: '#9ca3af', fontSize: 10, letterSpacing: 2, marginBottom: 8 }}>PREVIOUS SUBMISSIONS</div>
                {existingSubs.map(s => {
                  const _core     = (s.remarks || '').trim().replace(/^\[ADMIN\]\s*/i, '').trim();
                  const isLoss    = _core.startsWith('[LOSS]');
                  const isShort   = _core.startsWith('[SHORT]');
                  const isExcess  = _core.startsWith('[EXCESS]');
                  const typeLabel = isLoss   ? 'LOSS/UTILIZED'
                    : isShort  ? 'SHORT'
                    : isExcess ? 'EXCESS'
                    : (MODE_LABEL[s.type] || s.type?.toUpperCase());
                  const badgeBg   = isLoss   ? '#1c0e00'
                    : isShort  ? '#2d0f0f'
                    : isExcess ? '#0c1a2e'
                    : s.type === 'cms' ? '#2d1f00' : '#1e3a5f';
                  const badgeClr  = isLoss   ? '#f97316'
                    : isShort  ? '#f87171'
                    : isExcess ? '#3b82f6'
                    : s.type === 'cms' ? '#f59e0b' : '#60a5fa';
                  const cleanRemarks = s.remarks
                    ?.replace(/^\[(LOSS|SHORT|EXCESS)\]\s*/, '');
                  return (
                    <div key={s.id} style={{ background: '#0f1117', borderRadius: 8, padding: '10px 12px', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ color: isLoss ? '#f97316' : '#34d399', fontWeight: 700, fontSize: 13 }}>{fmt(s.amount)}</div>
                        <div style={{ color: '#6b7280', fontSize: 11 }}>
                          {typeLabel} · {s.submission_date}{!isLoss && ` · ${s.ref_number || 'No ref'}`}
                        </div>
                        {cleanRemarks && <div style={{ color: '#9ca3af', fontSize: 11 }}>{cleanRemarks}</div>}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                        <span style={{ background: badgeBg, color: badgeClr, borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>
                          {typeLabel}
                        </span>
                        {s.slip_url && <button onClick={() => setPreviewSlip(s.slip_url)} style={{ background: 'transparent', border: 'none', color: '#f59e0b', fontSize: 10, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>View Slip</button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}

          {/* Prior deposit fully matched — show info banner (form still available for Excess) */}
          {!isDirectSubmission && txn.has_prior_deposit && txn.prior_deposit_match && (
            <div style={{ background: '#0c1c14', border: '1px solid #34d399', borderRadius: 10, padding: 16, textAlign: 'center' }}>
              <div style={{ color: '#34d399', fontSize: 15, fontWeight: 700, marginBottom: 6 }}>✅ MATCHED VIA PRIOR DEPOSIT</div>
              <div style={{ color: '#4b5563', fontSize: 11 }}>
                Hub submitted {fmt(txn.direct_amt)} before this Excel upload — exact match confirmed. Use 💰 Excess below if additional amount was deposited.
              </div>
            </div>
          )}

          {/* Submission form — always shown */}
          {(()=> {
            return (
            <>
              {/* Prior deposit mismatch warning — show when hub submitted before Excel upload but amounts differ */}
              {!isDirectSubmission && txn.has_prior_deposit && !txn.prior_deposit_match && (
                <div style={{ background: '#1c0a00', border: '1px solid #f87171', borderRadius: 10, padding: 12 }}>
                  <div style={{ color: '#f87171', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>
                    ⚠️ PRIOR DEPOSIT MISMATCH
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: 11, lineHeight: 1.5 }}>
                    Hub deposited <strong style={{ color: '#f87171' }}>{fmt(txn.direct_amt)}</strong> before
                    this Excel was uploaded, but the Excel DIFF is <strong style={{ color: '#f3f4f6' }}>{fmt(txn.diff)}</strong>.
                    {' '}Gap: <strong style={{ color: '#f87171' }}>{fmt(Math.abs(txn.diff - txn.direct_amt))}</strong>
                    {txn.direct_amt > txn.diff
                      ? ' (excess — hub paid more than due)'
                      : ' — submit the remaining proof below.'}
                  </div>
                </div>
              )}

              <div style={{ color: '#9ca3af', fontSize: 10, letterSpacing: 2 }}>NEW SUBMISSION</div>

              {/* Full / Short / Excess / Loss toggle */}
              <div>
                <div style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, marginBottom: 6 }}>SUBMISSION TYPE</div>
                <div style={{ background: '#0f1117', borderRadius: 8, padding: 4, display: 'flex', gap: 4 }}>
                  {[['full', '✅ Full'], ['short', '⚠️ Short'], ['excess', '💰 Excess'], ['loss', '💸 Loss/Utilized']].map(([k, label]) => (
                    <button key={k} onClick={() => {
                      setSubmissionKind(k);
                      // Always clear amount when switching type — hub must enter manually
                      setForm(f => ({ ...f, amount: '' }));
                    }}
                      style={{ flex: 1, padding: '8px', borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: 1, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                        background: submissionKind === k
                          ? (k === 'full' ? '#34d399' : k === 'short' ? '#f87171' : k === 'excess' ? '#3b82f6' : '#f97316')
                          : 'transparent',
                        color: submissionKind === k ? '#0f1117' : '#6b7280' }}>
                      {label}
                    </button>
                  ))}
                </div>
                {/* Loss helper text */}
                {submissionKind === 'loss' && (
                  <div style={{ marginTop: 8, background: '#1c0e00', border: '1px solid #f9731640', borderRadius: 8, padding: '8px 12px', color: '#fb923c', fontSize: 11, lineHeight: 1.5 }}>
                    💸 Use this when the full remaining amount was <strong>lost</strong> (COD loss, rider absconding) or <strong>utilized by the hub</strong> (paid to rider, operational use). Mandatory remarks required.
                  </div>
                )}
              </div>

              {/* Payment method — hidden for Loss submissions */}
              {submissionKind !== 'loss' && (
                <div>
                  <div style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, marginBottom: 6 }}>PAYMENT METHOD</div>
                  <div style={{ background: '#0f1117', borderRadius: 8, padding: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {PAYMENT_MODES.map(({ key, label }) => (
                      <button key={key} onClick={() => setMode(key)}
                        style={{ padding: '9px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: 1, border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                          background: mode === key ? '#f59e0b' : 'transparent',
                          color:      mode === key ? '#0f1117' : '#6b7280' }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Amount + Date */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 4 }}>
                    AMOUNT (₹)*
                    {(submissionKind === 'full' || submissionKind === 'loss') && !isDirectSubmission && (
                      <span style={{ color: submissionKind === 'loss' ? '#f97316' : '#34d399', marginLeft: 6, fontSize: 9 }}>AUTO-FILLED · LOCKED</span>
                    )}
                  </label>
                  {(() => {
                    const isLocked = (submissionKind === 'full' || submissionKind === 'loss') && !isDirectSubmission;
                    const displayVal = isLocked
                      ? String(txn.effective_remaining ?? txn.remaining ?? '')
                      : form.amount;
                    return (
                      <input type="number"
                        value={displayVal}
                        onChange={e => !isLocked && setForm(f => ({ ...f, amount: e.target.value }))}
                        readOnly={isLocked}
                        style={{ ...inputStyle, opacity: isLocked ? 0.6 : 1, cursor: isLocked ? 'not-allowed' : 'text',
                          borderColor: submissionKind === 'loss' ? '#f9731650' : submissionKind === 'full' ? '#34d39950' : '#374151' }} />
                    );
                  })()}
                </div>
                <div>
                  <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 4 }}>DATE*</label>
                  <input type="date" value={form.date}
                    onChange={e => setForm(f => ({ ...f, date: e.target.value }))} style={inputStyle} />
                </div>
              </div>

              {/* Ref number — hidden for Loss declarations */}
              {submissionKind !== 'loss' && (
                <div>
                  <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 4 }}>
                    {mode === 'cms' ? 'CMS REF / SLIP NO' : 'UTR / REF NUMBER'}{' '}
                    <span style={{ color: '#f87171' }}>* REQUIRED</span>
                  </label>
                  <input type="text"
                    placeholder={mode === 'cms' ? 'Enter CMS reference' : 'Enter UTR number'}
                    value={form.ref} onChange={e => setForm(f => ({ ...f, ref: e.target.value }))}
                    style={{ ...inputStyle, borderColor: !form.ref.trim() ? '#f87171' : '#374151' }} />
                </div>
              )}

              {/* Submitted by */}
              <div>
                <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 4 }}>YOUR NAME</label>
                <input type="text" placeholder="Who is submitting?" value={form.submittedBy}
                  onChange={e => setForm(f => ({ ...f, submittedBy: e.target.value }))} style={inputStyle} />
              </div>

              {/* Remarks — required for Short, Excess and Loss */}
              <div>
                <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 4 }}>
                  REMARKS {(submissionKind === 'short' || submissionKind === 'excess')
                    ? <span style={{ color: '#f87171' }}>* REQUIRED</span>
                    : submissionKind === 'loss'
                      ? <span style={{ color: '#f97316' }}>* REQUIRED — describe what happened</span>
                      : <span style={{ color: '#4b5563' }}>(OPTIONAL)</span>}
                </label>
                <textarea
                  placeholder={
                    submissionKind === 'short'  ? 'Explain reason for short submission...'
                    : submissionKind === 'loss' ? 'Explain what happened (e.g. COD loss by rider, paid to rider from hub cash, rider absconded)...'
                    : submissionKind === 'excess' ? 'Mention which date\'s shortfall this covers...'
                    : 'Any notes...'
                  }
                  value={form.remarks}
                  onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
                  rows={submissionKind === 'loss' ? 3 : 2}
                  style={{ ...inputStyle, resize: 'none',
                    borderColor: (submissionKind === 'short' || submissionKind === 'excess' || submissionKind === 'loss') && !form.remarks.trim()
                      ? (submissionKind === 'loss' ? '#f97316' : '#f87171')
                      : '#374151' }} />
              </div>

              {/* Slip upload — hidden for Loss declarations (no payment was made) */}
              {submissionKind !== 'loss' && (
                <div>
                  <label style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1, display: 'block', marginBottom: 4 }}>
                    DEPOSIT SLIP / SCREENSHOT{' '}
                    <span style={{ color: '#f87171' }}>* REQUIRED</span>
                  </label>
                  <div onClick={() => fileRef.current.click()}
                    style={{
                      border: `1px dashed ${!slipFile ? '#f87171' : '#34d399'}`,
                      borderRadius: 8, padding: 14, textAlign: 'center', cursor: 'pointer', color: '#6b7280', fontSize: 12,
                    }}>
                    {slipPreview
                      ? <img src={slipPreview} alt="slip" style={{ maxHeight: 100, borderRadius: 6 }} />
                      : <span style={{ color: '#f87171' }}>📎 Upload deposit slip (required)</span>}
                  </div>
                  <input type="file" ref={fileRef} accept="image/*,application/pdf" onChange={handleFile} style={{ display: 'none' }} />
                </div>
              )}

              {/* Confirmation panel — shown after clicking submit */}
              {showConfirm ? (() => {
                const effectiveRem  = txn.effective_remaining ?? txn.remaining ?? 0;
                const submittingAmt = (submissionKind === 'full' || submissionKind === 'loss') ? effectiveRem : parseFloat(form.amount || 0);
                const shortAmt  = Math.max(0, effectiveRem - submittingAmt);
                const excessAmt = Math.max(0, submittingAmt - effectiveRem);
                const typeLabel = submissionKind === 'loss' ? '💸 Loss/Utilized' : submissionKind === 'short' ? '⚠️ Short' : submissionKind === 'excess' ? '💰 Excess' : '✅ Full';
                const btnBg     = submissionKind === 'loss' ? '#f97316' : submissionKind === 'excess' ? '#3b82f6' : '#f59e0b';
                return (
                  <div style={{ background: '#0f1117', border: `1px solid ${submissionKind === 'loss' ? '#f9731640' : '#f59e0b40'}`, borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ color: submissionKind === 'loss' ? '#f97316' : '#f59e0b', fontWeight: 700, fontSize: 12, letterSpacing: 1 }}>CONFIRM SUBMISSION</div>
                    {submissionKind === 'loss' && (
                      <div style={{ background: '#1c0e00', border: '1px solid #f9731640', borderRadius: 8, padding: '8px 12px', color: '#fb923c', fontSize: 11 }}>
                        ⚠️ You are declaring ₹{Number(submittingAmt).toLocaleString('en-IN')} as <strong>lost / utilized by hub</strong>. This will clear the pending amount. This action cannot be undone.
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {[
                        ['TYPE',       typeLabel],
                        submissionKind === 'loss'
                          ? ['WRITE-OFF', fmt(submittingAmt)]
                          : ['PAYMENT',  MODE_LABEL[mode] || mode.toUpperCase()],
                        ['AMOUNT',     fmt(submittingAmt)],
                        submissionKind === 'excess'
                          ? ['EXCESS AMT', excessAmt > 0 ? fmt(excessAmt) : '–']
                          : submissionKind === 'loss'
                            ? ['CLEARS PENDENCY', '✅ Yes']
                            : ['SHORT AMT', shortAmt > 0 ? fmt(shortAmt) : '₹0 (cleared)'],
                        ['DATE',       form.date],
                        ...(submissionKind !== 'loss' ? [['REF / UTR', form.ref || '—']] : []),
                      ].map(([l, v]) => (
                        <div key={l}>
                          <div style={{ color: '#4b5563', fontSize: 10, letterSpacing: 1 }}>{l}</div>
                          <div style={{ color: '#f3f4f6', fontSize: 12, fontWeight: 700, marginTop: 2 }}>{v}</div>
                        </div>
                      ))}
                    </div>
                    {form.remarks?.trim() && (
                      <div style={{ background: '#1a1f2e', borderRadius: 8, padding: '8px 12px', color: '#9ca3af', fontSize: 12 }}>
                        <span style={{ color: '#6b7280', fontSize: 10, display: 'block', marginBottom: 2 }}>REMARKS</span>
                        {form.remarks}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button onClick={handleSubmit} disabled={submitting}
                        style={{ flex: 1, padding: 12, border: 'none', borderRadius: 8, fontWeight: 900, fontSize: 12, letterSpacing: 2, cursor: submitting ? 'wait' : 'pointer', fontFamily: 'inherit',
                          background: btnBg, color: '#0f1117' }}>
                        {submitting ? 'SUBMITTING...' : '✓ CONFIRM & SUBMIT'}
                      </button>
                      <button onClick={() => setShowConfirm(false)} disabled={submitting}
                        style={{ padding: '12px 18px', border: '1px solid #2a2f3e', borderRadius: 8, background: 'transparent', color: '#9ca3af', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                        ← BACK
                      </button>
                    </div>
                  </div>
                );
              })() : (
                <button onClick={requestConfirm}
                  style={{ width: '100%', padding: 13, border: 'none', borderRadius: 8, fontWeight: 900, fontSize: 13, letterSpacing: 2, cursor: 'pointer', fontFamily: 'inherit',
                    background: submissionKind === 'loss' ? '#f97316' : submissionKind === 'excess' ? '#3b82f6' : '#f59e0b',
                    color: '#0f1117' }}>
                  {submissionKind === 'loss' ? '💸 DECLARE LOSS / UTILIZED' : submissionKind === 'excess' ? '💰 SUBMIT EXCESS PAYMENT' : 'SUBMIT PROOF'}
                </button>
              )}
            </>
            );
          })()}
        </div>
      </div>
      <SlipPreviewModal url={previewSlip} onClose={() => setPreviewSlip(null)} />
    </div>
  );
}

// ─── Main Portal ───────────────────────────────────────────────────────────────
export default function HubPortal({ hubData, uploadMeta, user, onLogin, onLogout }) {
  // Gate: show login screen if no authenticated user
  if (!user) return <LoginScreen onLogin={onLogin} />;

  return <HubPortalInner hubData={hubData} uploadMeta={uploadMeta} user={user} onLogout={onLogout} />;
}

function HubPortalInner({ hubData, uploadMeta, user, onLogout }) {
  // Hub is fixed to the logged-in user's assigned hub — no dropdown needed
  const selectedHub = user.hub_name;

  const [transactions, setTransactions] = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [activeTxn,    setActiveTxn]    = useState(null);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterMonth,  setFilterMonth]  = useState('All');
  // directDate: the missing-day date currently open in the modal (null = closed)
  const [activeDirectDate,  setActiveDirectDate]  = useState(null);
  // directSubs: { 'YYYY-MM-DD': [...submissions] }
  const [directSubs,        setDirectSubs]        = useState({});
  const [loadingDirectSubs, setLoadingDirectSubs] = useState(false);
  // All non-direct submissions for this hub — used client-side to match orphaned
  // submissions (stale transaction_id) that the DB view won't count.
  const [hubAllSubs,  setHubAllSubs]  = useState([]);
  // Submissions flagged by admin for re-submission (review_status = 'flagged')
  const [flaggedSubs, setFlaggedSubs] = useState([]);
  // After delete-and-resubmit, open modal for this transaction DB UUID once augmented recomputes.
  // Using ID (not date) avoids opening the wrong row when two rows share the same date.
  const [openTxnId, setOpenTxnId] = useState(null);

  // Portal-level tab: 'submit' | 'summary' | 'history'
  const [section,     setSection]     = useState('payment'); // 'cod' | 'payment'
  const [portalTab,   setPortalTab]   = useState('submit');
  const [previewSlip, setPreviewSlip] = useState(null);  // slip URL for inline preview
  // Submission History tab filters
  const [histMonth,    setHistMonth]    = useState('All');
  const [histKind,     setHistKind]     = useState('All');
  const [histOpenOnly, setHistOpenOnly] = useState(false); // only show subs for open (unpaid) dates

  useEffect(() => {
    if (!selectedHub || !uploadMeta) return;
    // Run both fetches in parallel
    fetchAll();
  }, [selectedHub, uploadMeta]); // eslint-disable-line

  async function fetchAll() {
    setLoading(true);
    setLoadingDirectSubs(true);
    try {
      const [txnData, subsData, allSubsData] = await Promise.all([
        getTransactionsForHub(selectedHub, uploadMeta.id),
        getDirectSubmissions(uploadMeta.id, selectedHub),
        getAllSubmissionsForHub(selectedHub),
      ]);
      setTransactions(txnData);
      setDirectSubs(subsData);
      setHubAllSubs(allSubsData.filter(s => !s.is_direct));
      setFlaggedSubs(allSubsData.filter(s => s.review_status === 'flagged'));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setLoadingDirectSubs(false);
    }
  }

  async function handleDeleteAndResubmit(s) {
    try {
      await deleteSubmission(s.id, 'hub_delete_and_resubmit');
      // Optimistically remove from flaggedSubs so banner disappears
      setFlaggedSubs(prev => prev.filter(f => f.id !== s.id));
      if (s.is_direct) {
        // Refresh direct subs then open the direct-date modal
        await fetchAll();
        setActiveDirectDate(s.txn_date);
      } else {
        // Refresh all data, then open transaction modal via useEffect once augmented recomputes.
        // Use the transaction DB UUID for precise matching — avoids opening the wrong row
        // when two transactions share the same collection_date (split-date scenario).
        await fetchAll();
        setOpenTxnId(s.transaction_id);
      }
    } catch (e) { alert('Failed: ' + e.message); }
  }

  async function refreshDirectSubs() {
    try {
      const subs = await getDirectSubmissions(uploadMeta.id, selectedHub);
      setDirectSubs(subs);
    } catch (e) { console.error(e); }
  }

  // Build a date → amount map from today_direct (Untracked Days) submissions.
  // Some of these dates may now exist as regular Excel rows (e.g. March 5th was
  // submitted as an untracked day, then the next Excel upload covered it).
  // We merge the direct amount into each matching transaction row so the
  // per-row remaining figure is correct.
  const directSubsMap = useMemo(() => {
    const map = {};
    for (const [date, subs] of Object.entries(directSubs)) {
      map[date] = subs.reduce((s, x) => s + Number(x.amount || 0), 0);
    }
    return map;
  }, [directSubs]);

  // Augment every transaction with effective_submitted and effective_remaining
  // so the row reflects ALL money submitted for that date (Excel subs + direct subs).
  // Uses hubAllSubs for client-side submission matching so that orphaned submissions
  // (stale transaction_id from a pre-in-place-fix upload) are correctly counted.
  const augmented = useMemo(() => {
    // Count transactions per date to detect split dates (same hub, same date, different amounts)
    const datesCount = {};
    transactions.forEach(t => { datesCount[t.collection_date] = (datesCount[t.collection_date] || 0) + 1; });

    // Build quick lookups from hubAllSubs
    const txnById   = {};
    transactions.forEach(t => { txnById[t.id] = t; });

    // sub by exact transaction_id
    const subsByTxnId = {};
    // sub by hub|date (with date recovered if join failed)
    const subsByDate  = {};
    // truly orphaned (no recoverable date) — for amount fallback
    const orphaned    = [];

    hubAllSubs.forEach(s => {
      // Tier 1: group by exact transaction_id
      if (s.transaction_id) {
        if (!subsByTxnId[s.transaction_id]) subsByTxnId[s.transaction_id] = [];
        subsByTxnId[s.transaction_id].push(s);
      }
      // Tier 2: group by date (from join or from loaded transactions)
      const effDate = s.txn_date || txnById[s.transaction_id]?.collection_date;
      if (effDate) {
        if (!subsByDate[effDate]) subsByDate[effDate] = [];
        subsByDate[effDate].push(s);
      } else {
        // Tier 3: truly orphaned — no recoverable date
        orphaned.push(s);
      }
    });

    return transactions.map(t => {
      const directAmt = directSubsMap[t.collection_date] || 0;
      const diff      = Number(t.diff || 0);
      const base      = Number(t.amount_to_collect || diff);

      // Determine which submissions belong to this transaction row (client-side matching)
      const isSplitDate = datesCount[t.collection_date] > 1;
      let matchedSubs;
      if (isSplitDate) {
        // Split date: primary = exact txn_id, fallback = txn_amount_to_collect match
        const exact = subsByTxnId[t.id] || [];
        if (exact.length > 0) {
          matchedSubs = exact;
        } else {
          const dateSubs = subsByDate[t.collection_date] || [];
          matchedSubs = dateSubs.filter(s =>
            Number(s.txn_amount_to_collect ?? txnById[s.transaction_id]?.amount_to_collect) === Number(t.amount_to_collect)
          );
        }
      } else {
        // Single date: use all subs for this date; last resort = orphaned by amount
        const dateSubs = subsByDate[t.collection_date] || [];
        matchedSubs = dateSubs.length > 0
          ? dateSubs
          : orphaned.filter(s => Number(s.amount) === Number(t.amount_to_collect) && t.amount_to_collect > 0);
      }

      const clientSubmitted    = matchedSubs.reduce((acc, s) => acc + Number(s.amount || 0), 0);
      const effectiveSubmitted = clientSubmitted + directAmt;
      const effectiveRemaining = Math.max(0, base - effectiveSubmitted);
      const hasPriorDeposit    = directAmt > 0;
      const priorDepositMatch  = hasPriorDeposit && directAmt === base;
      return {
        ...t,
        total_submitted:      clientSubmitted,
        sub_count:            matchedSubs.length,
        remaining:            effectiveRemaining,
        effective_submitted:  effectiveSubmitted,
        effective_remaining:  effectiveRemaining,
        direct_amt:           directAmt,
        has_prior_deposit:    hasPriorDeposit,
        prior_deposit_match:  priorDepositMatch,
        _matchedSubs:         matchedSubs, // passed to modal for display
      };
    });
  }, [transactions, directSubsMap, hubAllSubs]); // eslint-disable-line

  // Merge same-date transaction rows into one display row per date.
  // Same rules as admin/manager hub summary:
  //   • All rows on a date are Closed → hidden from hub portal
  //   • At least one row is Open/blank → show one merged row summing ALL rows
  const CLOSED_LS = ['CLOSED', 'Closed', 'closed'];
  const mergedAugmented = useMemo(() => {
    const dateMap = {};
    augmented.forEach(t => {
      const key = t.collection_date || '__no_date__';
      if (!dateMap[key]) dateMap[key] = [];
      dateMap[key].push(t);
    });
    return Object.values(dateMap)
      .filter(group => group.some(t => !CLOSED_LS.includes(t.line_status)))
      .map(group => {
      if (group.length === 1) return group[0];
      const directAmt      = group[0].direct_amt; // same value for all same-date txns
      const totalCollect   = group.reduce((s, t) => s + Number(t.amount_to_collect || t.diff || 0), 0);
      const totalSubmitted = group.reduce((s, t) => s + Number(t.effective_submitted || 0), 0);
      const totalRemain    = Math.max(0, totalCollect - totalSubmitted);
      const allSubs        = group.flatMap(t => t._matchedSubs || []);
      // For submission: prefer the first transaction that still has a remaining balance
      const primaryTxn     = group.find(t => Number(t.effective_remaining) > 0) || group[0];
      return {
        ...group[0],
        id:                  primaryTxn.id,
        amount_to_collect:   totalCollect,
        diff:                totalCollect,
        total_submitted:     totalSubmitted,
        remaining:           totalRemain,
        effective_submitted: totalSubmitted,
        effective_remaining: totalRemain,
        direct_amt:          directAmt,
        has_prior_deposit:   directAmt > 0,
        prior_deposit_match: directAmt > 0 && directAmt === totalCollect,
        sub_count:           new Set(allSubs.map(s => s.id)).size,
        _matchedSubs:        allSubs,
        _mergedIds:          group.map(t => t.id),
        _isMerged:           true,
      };
    }).sort((a, b) => (a.collection_date || '').localeCompare(b.collection_date || ''));
  }, [augmented]); // eslint-disable-line

  // Once mergedAugmented recomputes after a delete-and-resubmit, open the correct modal.
  useEffect(() => {
    if (!openTxnId) return;
    const txn = mergedAugmented.find(t =>
      t.id === openTxnId || (t._mergedIds && t._mergedIds.includes(openTxnId))
    );
    if (txn) { setActiveTxn(txn); setOpenTxnId(null); }
  }, [mergedAugmented, openTxnId]); // eslint-disable-line

  // Unique sorted months for the month filter pill bar
  const availableMonths = useMemo(() => {
    const seen = new Set();
    mergedAugmented.forEach(t => { if (t.month) seen.add(t.month); });
    return Array.from(seen).sort();
  }, [mergedAugmented]);

  // Reset month filter when hub changes
  const monthFiltered = useMemo(() =>
    filterMonth === 'All' ? mergedAugmented : mergedAugmented.filter(t => t.month === filterMonth),
    [mergedAugmented, filterMonth]
  );

  // Set of dates that exist in the uploaded Excel — used to separate
  // "direct submissions for Excel dates" from "future date deposits".
  const excelDateSet = useMemo(
    () => new Set(transactions.map(t => t.collection_date).filter(Boolean)),
    [transactions]
  );

  // Grand totals scoped to the active month filter.
  // totalSubmitted uses effective_submitted (= Excel subs + direct subs for the
  // SAME date) so future-date deposits are NOT mixed into the Excel pendency.
  const totalDiff      = useMemo(() => monthFiltered.reduce((s, t) => s + Number(t.amount_to_collect || t.diff || 0), 0), [monthFiltered]);
  const totalSubmitted = useMemo(() =>
    monthFiltered.reduce((s, t) => s + Number(t.effective_submitted || 0), 0),
    [monthFiltered]
  );
  const totalRemaining = useMemo(() => Math.max(0, totalDiff - totalSubmitted), [totalDiff, totalSubmitted]);

  // Future date deposits — direct submissions for dates NOT in the Excel file.
  // These have no matching totalDiff entry so they must be shown separately.
  const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const futureDatesTotal = useMemo(() =>
    Object.entries(directSubsMap)
      .filter(([date]) => {
        if (excelDateSet.has(date)) return false; // already merged into an Excel row
        if (filterMonth === 'All') return true;
        const [y, m] = date.split('-');
        return `${MONTH_LABELS[parseInt(m, 10) - 1]}-${String(y).slice(2)}` === filterMonth;
      })
      .reduce((s, [, amt]) => s + amt, 0),
    [directSubsMap, excelDateSet, filterMonth] // eslint-disable-line
  );

  // Summary card: total rows still not cleared
  const pendingCount   = useMemo(() => monthFiltered.filter(t => t.effective_remaining > 0).length, [monthFiltered]);
  const clearedCount   = useMemo(() => monthFiltered.filter(t => t.effective_remaining === 0 && Number(t.amount_to_collect || t.diff) > 0).length, [monthFiltered]);

  // Per-tab counts — finer breakdown of the "pending" bucket
  const untouchedCount = useMemo(() =>
    monthFiltered.filter(t => t.effective_remaining > 0 && t.effective_submitted === 0 && (t.sub_count || 0) === 0).length,
    [monthFiltered]);
  const zeroSubCount   = useMemo(() =>
    monthFiltered.filter(t => t.effective_remaining > 0 && t.effective_submitted === 0 && (t.sub_count || 0) > 0).length,
    [monthFiltered]);
  const partialCount   = useMemo(() =>
    monthFiltered.filter(t => t.effective_submitted > 0 && t.effective_remaining > 0).length,
    [monthFiltered]);

  const filtered = useMemo(() => monthFiltered.filter(t => {
    if (filterStatus === 'pending')  return t.effective_remaining > 0 && t.effective_submitted === 0 && (t.sub_count || 0) === 0;
    if (filterStatus === 'zero_sub') return t.effective_remaining > 0 && t.effective_submitted === 0 && (t.sub_count || 0) > 0;
    if (filterStatus === 'partial')  return t.effective_submitted > 0 && t.effective_remaining > 0;
    if (filterStatus === 'cleared')  return t.effective_remaining === 0;
    return true;
  }), [monthFiltered, filterStatus]);

  // Dates not yet in Excel — only recompute when transactions changes
  const missingDates = useMemo(() => getMissingDates(transactions), [transactions]);

  // ── History tab data ──────────────────────────────────────────────────────
  // Combine regular + direct submissions, newest first
  const allSubsFlat = useMemo(() => {
    const dir = Object.values(directSubs).flat();
    return [...hubAllSubs, ...dir].sort((a, b) =>
      (b.submission_date || '').localeCompare(a.submission_date || '')
    );
  }, [hubAllSubs, directSubs]);

  const histMonths = useMemo(() => {
    const seen = new Set();
    allSubsFlat.forEach(s => { const m = histSubMonth(s); if (m) seen.add(m); });
    return ['All', ...[...seen].sort().reverse()];
  }, [allSubsFlat]); // eslint-disable-line

  // Set of collection_date strings that are still open (have remaining balance in uploaded file)
  const openDatesSet = useMemo(() => {
    const s = new Set();
    mergedAugmented.forEach(t => {
      if (Number(t.effective_remaining) > 0 && t.collection_date) s.add(t.collection_date);
    });
    return s;
  }, [mergedAugmented]); // eslint-disable-line

  const filteredHistSubs = useMemo(() => allSubsFlat.filter(s => {
    if (histMonth   !== 'All' && histSubMonth(s) !== histMonth) return false;
    if (histKind    !== 'All' && histSubKind(s)  !== histKind)  return false;
    if (histOpenOnly && !openDatesSet.has(s.txn_date))          return false;
    return true;
  }), [allSubsFlat, histMonth, histKind, histOpenOnly, openDatesSet]); // eslint-disable-line

  // hubData is passed for context but the hub is fixed from the logged-in user

  return (
    <div style={{ minHeight: '100vh', background: '#0b0e14', fontFamily: "'DM Mono', monospace" }}>

      {/* Top bar */}
      <div style={{ background: '#0f1117', borderBottom: '1px solid #2a2f3e', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ background: '#f59e0b', borderRadius: 8, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ color: '#0f1117', fontWeight: 900, fontSize: 16 }}>W</span>
        </div>
        <div>
          <div style={{ color: '#f59e0b', fontWeight: 700, fontSize: 14, letterSpacing: 3 }}>WHEELEY</div>
          <div style={{ color: '#6b7280', fontSize: 11 }}>HUB CASH SUBMISSION PORTAL</div>
        </div>
        {/* ── Section switcher (only shown when logged in) ── */}
        {user && (
          <div style={{ display: 'flex', background: '#0b0e14', border: '1px solid #1e2433', borderRadius: 10, padding: 3, gap: 2, marginLeft: 16 }}>
            {[['cod', '📦 COD Reconciliation'], ['payment', '💳 Payment Sheet']].map(([k, label]) => (
              <button key={k} onClick={() => setSection(k)}
                style={{
                  padding: '5px 12px', border: 'none', borderRadius: 8, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, transition: 'all .15s',
                  background: section === k ? (k === 'payment' ? '#1e3a5f' : '#1c1a00') : 'transparent',
                  color: section === k ? (k === 'payment' ? '#60a5fa' : '#f59e0b') : '#4b5563',
                  boxShadow: section === k ? '0 1px 4px #00000060' : 'none',
                }}>
                {label}
              </button>
            ))}
          </div>
        )}
        {/* Logged-in hub badge */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ background: '#1a1f2e', border: '1px solid #2a2f3e', borderRadius: 8, padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#f59e0b', fontSize: 12 }}>🏢</span>
            <div>
              <div style={{ color: '#f3f4f6', fontSize: 12, fontWeight: 700 }}>{selectedHub}</div>
              <div style={{ color: '#4b5563', fontSize: 10 }}>👤 {user.username}</div>
            </div>
          </div>
          {uploadMeta && (
            <div style={{ color: '#4b5563', fontSize: 11 }}>
              File: <strong style={{ color: '#6b7280' }}>{uploadMeta.upload_date || uploadMeta.date}</strong>
            </div>
          )}
          <button onClick={onLogout}
            style={{ background: 'transparent', border: '1px solid #374151', borderRadius: 8, color: '#6b7280', fontSize: 11, fontWeight: 700, padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 1 }}>
            Sign Out
          </button>
        </div>
      </div>

      {/* ── Payment Sheet section ── */}
      {section === 'payment' && user && <PaymentSheet isAdmin={false} user={user} />}

      {section === 'cod' && (<>
      {/* ── Portal Tabs ───────────────────────────────────────────────────────── */}
      <div style={{ background: '#0f1117', borderBottom: '1px solid #1e2433', padding: '0 24px', display: 'flex', gap: 0 }}>
        {[
          ['submit',  '📋 Submit Proof'],
          ['summary', '📊 Hub Summary'],
          ['history', '🕐 Submission History'],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setPortalTab(k)}
            style={{
              padding: '13px 22px', background: 'transparent', border: 'none',
              borderBottom: `2px solid ${portalTab === k ? '#f59e0b' : 'transparent'}`,
              color: portalTab === k ? '#f59e0b' : '#6b7280',
              fontSize: 12, fontWeight: 700, letterSpacing: 1, cursor: 'pointer',
              fontFamily: 'inherit', transition: 'all .2s',
            }}>
            {label}
          </button>
        ))}
      </div>

      {portalTab === 'submit' && (
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '28px 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Hub info banner — replaces the hub dropdown now that login is enforced */}
        {!uploadMeta && (
          <div style={{ background: '#1c1400', border: '1px solid #f59e0b30', borderRadius: 12, padding: '14px 18px', color: '#6b7280', fontSize: 12 }}>
            ⏳ No report uploaded yet. Future date submissions can still be recorded above.
          </div>
        )}

        {/* ── FLAGGED SUBMISSIONS BANNER ───────────────────────────────────── */}
        {flaggedSubs.length > 0 && (
          <div style={{ background: 'linear-gradient(135deg, #1c0808, #1a0f0f)', border: '2px solid #f87171', borderRadius: 12, padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <span style={{ fontSize: 20 }}>⚠️</span>
              <div>
                <div style={{ color: '#f87171', fontWeight: 900, fontSize: 14, letterSpacing: 1 }}>
                  {flaggedSubs.length} SUBMISSION{flaggedSubs.length > 1 ? 'S' : ''} NEED CORRECTION
                </div>
                <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>
                  Admin has flagged the below entries — please delete and resubmit with correct details
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {flaggedSubs.map(s => {
                const typeMap = { cms: 'CMS', neft_flipkart: 'NEFT-FK', neft_wheeley: 'NEFT-WH' };
                const _c       = (s.remarks || '').trim().replace(/^\[ADMIN\]\s*/i, '').trim();
                const isLoss   = _c.startsWith('[LOSS]');
                const isShort  = _c.startsWith('[SHORT]');
                const isExcess = _c.startsWith('[EXCESS]');
                const typeLabel = isLoss ? 'LOSS/UTILIZED' : isExcess ? 'EXCESS' : (typeMap[s.type] || s.type?.toUpperCase() || '—');
                return (
                  <div key={s.id} style={{ background: '#0f0a0a', border: '1px solid #7f1d1d', borderRadius: 10, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
                        <span style={{ color: '#f3f4f6', fontWeight: 700, fontSize: 13 }}>₹{fmt(Number(s.amount))}</span>
                        <span style={{ background: '#1e2030', color: '#60a5fa', borderRadius: 4, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>{typeLabel}</span>
                        {isShort && <span style={{ color: '#f87171', fontSize: 10, fontWeight: 700 }}>· SHORT</span>}
                        <span style={{ color: '#6b7280', fontSize: 11 }}>
                          {s.is_direct ? 'Direct deposit' : 'For date'}: <span style={{ color: '#9ca3af' }}>{s.txn_date || '—'}</span>
                        </span>
                        <span style={{ color: '#6b7280', fontSize: 11 }}>
                          Sub date: <span style={{ color: '#9ca3af' }}>{s.submission_date}</span>
                        </span>
                        {s.ref_number && <span style={{ color: '#6b7280', fontSize: 11 }}>Ref: <span style={{ color: '#9ca3af' }}>#{s.ref_number}</span></span>}
                      </div>
                      {s.review_note && (
                        <div style={{ background: '#2d0f0f', border: '1px solid #f8717140', borderRadius: 7, padding: '7px 10px', marginTop: 4 }}>
                          <span style={{ color: '#f87171', fontSize: 10, fontWeight: 700, letterSpacing: 1 }}>ADMIN NOTE: </span>
                          <span style={{ color: '#fca5a5', fontSize: 12 }}>{s.review_note}</span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteAndResubmit(s)}
                      style={{ background: '#f87171', color: '#0f1117', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 12, fontWeight: 900, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', letterSpacing: 1 }}>
                      🔄 Archive & Resubmit
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── UNTRACKED DAYS section ─────────────────────────────────────────── */}
        {selectedHub && missingDates.length > 0 && (
          <div style={{ background: 'linear-gradient(135deg, #0c1f0f, #1a1f2e)', border: '1px solid #34d39940', borderRadius: 12, padding: 18 }}>
            <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ color: '#34d399', fontWeight: 700, fontSize: 13, letterSpacing: 1 }}>
                  SUBMIT UNTRACKED COD DEPOSITS
                </div>
                <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>
                  Dates not yet in the uploaded report — click a date to submit
                </div>
              </div>
              {loadingDirectSubs && <div style={{ color: '#4b5563', fontSize: 11 }}>Loading…</div>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {missingDates.map(date => {
                const subs      = directSubs[date] || [];
                const subCount  = subs.length;
                const totalAmt  = subs.reduce((s, x) => s + Number(x.amount || 0), 0);
                const hasShort  = subs.some(s => (s.remarks || '').trim().replace(/^\[ADMIN\]\s*/i, '').trim().startsWith('[SHORT]'));
                const hasLoss   = subs.some(s => (s.remarks || '').trim().replace(/^\[ADMIN\]\s*/i, '').trim().startsWith('[LOSS]'));
                const isToday   = date === todayStr();

                return (
                  <div key={date} onClick={() => setActiveDirectDate(date)}
                    style={{ background: '#0f1117', border: `1px solid ${subCount > 0 ? '#34d39940' : '#2a2f3e'}`, borderRadius: 10, padding: '12px 16px', cursor: 'pointer', display: 'grid', gridTemplateColumns: '140px 1fr 1fr 100px', gap: 12, alignItems: 'center' }}>

                    {/* Date */}
                    <div>
                      <div style={{ color: '#f3f4f6', fontSize: 12, fontWeight: 700 }}>{formatDate(date)}</div>
                      {isToday && (
                        <span style={{ background: '#1e3a5f', color: '#60a5fa', borderRadius: 4, padding: '1px 6px', fontSize: 9, fontWeight: 700 }}>TODAY</span>
                      )}
                    </div>

                    {/* Amount submitted */}
                    <div>
                      <div style={{ color: '#6b7280', fontSize: 10 }}>SUBMITTED</div>
                      <div style={{ color: subCount > 0 ? '#34d399' : '#4b5563', fontSize: 13, fontWeight: 700 }}>
                        {subCount > 0 ? fmt(totalAmt) : '—'}
                      </div>
                    </div>

                    {/* Count */}
                    <div>
                      <div style={{ color: '#6b7280', fontSize: 10 }}>ENTRIES</div>
                      <div style={{ color: '#9ca3af', fontSize: 13, fontWeight: 700 }}>
                        {subCount > 0 ? `${subCount} sub${subCount !== 1 ? 's' : ''}` : '—'}
                      </div>
                    </div>

                    {/* Status */}
                    <div style={{ textAlign: 'right' }}>
                      {subCount > 0 ? (
                        <span style={{
                          background: hasLoss ? '#1c0e00' : hasShort ? '#3b0f0f' : '#0c1c14',
                          color:      hasLoss ? '#f97316' : hasShort ? '#f87171' : '#34d399',
                          borderRadius: 6, padding: '3px 8px', fontSize: 10, fontWeight: 700 }}>
                          {hasLoss ? 'LOSS DECLARED' : hasShort ? 'SHORT' : 'SUBMITTED'}
                        </span>
                      ) : (
                        <span style={{ background: '#1c0f0f', color: '#f87171', borderRadius: 6, padding: '3px 8px', fontSize: 10, fontWeight: 700 }}>
                          PENDING
                        </span>
                      )}
                      <div style={{ color: '#374151', fontSize: 16, marginTop: 4 }}>→</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Month filter ──────────────────────────────────────────────────── */}
        {selectedHub && availableMonths.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ color: '#6b7280', fontSize: 10, letterSpacing: 2, whiteSpace: 'nowrap' }}>MONTH</span>
            {['All', ...availableMonths].map(m => (
              <button key={m} onClick={() => setFilterMonth(m)}
                style={{ padding: '5px 14px', borderRadius: 20, border: '1px solid', fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                  borderColor: filterMonth === m ? '#6366f1' : '#2a2f3e',
                  background:  filterMonth === m ? '#6366f1' : 'transparent',
                  color:       filterMonth === m ? '#fff' : '#6b7280' }}>
                {m}
              </button>
            ))}
          </div>
        )}

        {/* ── Pendency summary ──────────────────────────────────────────────── */}
        {selectedHub && transactions.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

            {/* Excel-based pendency */}
            <div style={{ background: 'linear-gradient(135deg, #1c1400, #1a1f2e)', border: '1px solid #f59e0b40', borderRadius: 12, padding: 18 }}>
              <div style={{ color: '#9ca3af', fontSize: 11, letterSpacing: 2, marginBottom: 12 }}>
                PENDENCY SUMMARY — {selectedHub}{filterMonth !== 'All' ? ` · ${filterMonth}` : ''}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                {[
                  ['TOTAL DUE',    fmt(totalDiff),      '#f3f4f6'],
                  ['SUBMITTED',    fmt(totalSubmitted),  '#34d399'],
                  ['REMAINING',    fmt(totalRemaining),  totalRemaining > 0 ? '#f87171' : '#34d399'],
                  ['PENDING ROWS', `${pendingCount} / ${transactions.length}`, '#f59e0b'],
                ].map(([l, v, c]) => (
                  <div key={l}>
                    <div style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1 }}>{l}</div>
                    <div style={{ color: c, fontSize: 15, fontWeight: 700, marginTop: 4 }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Future date deposits — separate card, not mixed into pendency */}
            {futureDatesTotal > 0 && (
              <div style={{ background: 'linear-gradient(135deg, #0c1f2e, #1a1f2e)', border: '1px solid #6366f140', borderRadius: 12, padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ color: '#6366f1', fontSize: 11, letterSpacing: 2, fontWeight: 700 }}>
                    📅 FUTURE DATE DEPOSITS
                  </div>
                  <div style={{ color: '#4b5563', fontSize: 10, marginTop: 3 }}>
                    Submitted for dates not yet in the uploaded report — tracked separately
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#6b7280', fontSize: 10, letterSpacing: 1 }}>TOTAL DEPOSITED</div>
                  <div style={{ color: '#818cf8', fontSize: 18, fontWeight: 900, marginTop: 2 }}>{fmt(futureDatesTotal)}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Transaction list ──────────────────────────────────────────────── */}
        {selectedHub && (
          <>
            {transactions.length > 0 && (
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  ['pending',  `Pending (${untouchedCount})`,   '#f87171', '#3b0f0f'],
                  ['zero_sub', `Zero (${zeroSubCount})`,        '#a78bfa', '#1c0d2e'],
                  ['partial',  `Partial (${partialCount})`,     '#f59e0b', '#2d1f00'],
                  ['cleared',  `Cleared (${clearedCount})`,     '#34d399', '#0c1c14'],
                  ['all',      `All (${monthFiltered.length})`, '#9ca3af', '#1a1f2e'],
                ].map(([k, label, activeColor, activeBg]) => (
                  <button key={k} onClick={() => setFilterStatus(k)}
                    style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid', fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: 'pointer', fontFamily: 'inherit',
                      borderColor: filterStatus === k ? activeColor : '#2a2f3e',
                      background:  filterStatus === k ? activeBg  : 'transparent',
                      color:       filterStatus === k ? activeColor : '#6b7280' }}>
                    {label}
                  </button>
                ))}
              </div>
            )}

            {loading ? <Spinner /> : filtered.length === 0 ? (
              <div style={{ background: '#1a1f2e', border: '1px solid #2a2f3e', borderRadius: 12, padding: 32, textAlign: 'center', color: '#4b5563', fontSize: 13 }}>
                {transactions.length === 0
                  ? "No past transaction data found. Use the section above to submit pending days."
                  : filterStatus === 'pending'  ? `✅ No untouched rows${filterMonth !== 'All' ? ` in ${filterMonth}` : ''}!`
                  : filterStatus === 'zero_sub' ? `No zero-submission rows${filterMonth !== 'All' ? ` in ${filterMonth}` : ''}.`
                  : filterStatus === 'partial'  ? `No partial-submission rows${filterMonth !== 'All' ? ` in ${filterMonth}` : ''}.`
                  : filterMonth !== 'All'
                    ? `✅ All ${filterMonth} rows cleared!`
                    : '✅ All rows cleared!'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ color: '#6b7280', fontSize: 11, paddingLeft: 4 }}>{filtered.length} rows · click any row to submit proof</div>
                {filtered.map(txn => {
                  const baseAmt        = Number(txn.amount_to_collect || txn.diff) || 0;
                  const isCleared      = txn.effective_remaining === 0 && baseAmt > 0;
                  const hasSubmissions = txn.effective_submitted > 0;
                  const pct = baseAmt > 0 ? Math.min(100, (txn.effective_submitted / baseAmt) * 100) : 100;
                  return (
                    <div key={txn.id} onClick={() => setActiveTxn(txn)}
                      style={{ background: '#1a1f2e', border: `1px solid ${
                        txn.prior_deposit_match ? '#34d399' :
                        txn.has_prior_deposit ? '#f87171' :
                        isCleared ? '#34d39930' : hasSubmissions ? '#f59e0b30' : '#2a2f3e'
                      }`, borderRadius: 10, padding: '14px 16px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 10 }}>

                      {/* ── Row 1: summary ── */}
                      <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr 1fr 110px 90px', gap: 12, alignItems: 'center' }}>
                        <div>
                          <div style={{ color: '#9ca3af', fontSize: 12, fontWeight: 700 }}>
                            {txn.collection_date
                              ? new Date(txn.collection_date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })
                              : '—'}
                          </div>
                          <div style={{ color: '#4b5563', fontSize: 10, marginTop: 2 }}>{txn.month}</div>
                        </div>
                        <div><div style={{ color: '#6b7280', fontSize: 10 }}>TO COLLECT</div><div style={{ color: '#60a5fa', fontSize: 13, fontWeight: 700 }}>{fmt(baseAmt)}</div></div>
                        <div>
                          <div style={{ color: '#6b7280', fontSize: 10 }}>SUBMITTED</div>
                          <div style={{ color: '#34d399', fontSize: 13, fontWeight: 700 }}>{fmt(txn.effective_submitted)}</div>
                        </div>
                        <div><div style={{ color: '#6b7280', fontSize: 10 }}>REMAINING</div><div style={{ color: isCleared ? '#34d399' : '#f87171', fontSize: 13, fontWeight: 700 }}>{fmt(txn.effective_remaining)}</div></div>
                        <div>
                          <div style={{ background: '#0f1117', borderRadius: 4, height: 6 }}>
                            <div style={{ background: isCleared ? '#34d399' : '#f59e0b', width: `${pct}%`, height: '100%', borderRadius: 4 }} />
                          </div>
                          <div style={{ color: '#4b5563', fontSize: 10, marginTop: 3 }}>
                            {txn.sub_count} sub{txn.sub_count !== 1 ? 's' : ''}{txn.has_prior_deposit ? ' +prior' : ''}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ background: isCleared ? '#0c1c14' : hasSubmissions ? '#2d1f00' : '#1c0f0f', color: isCleared ? '#34d399' : hasSubmissions ? '#f59e0b' : '#f87171', borderRadius: 6, padding: '3px 8px', fontSize: 10, fontWeight: 700 }}>
                            {isCleared ? 'CLEARED' : hasSubmissions ? 'PARTIAL' : 'PENDING'}
                          </span>
                          <div style={{ color: '#374151', fontSize: 16, marginTop: 4 }}>→</div>
                        </div>
                      </div>

                      {/* ── Row 2: reconciliation detail ── */}
                      <div style={{ borderTop: '1px solid #2a2f3e', paddingTop: 8, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 10 }}>
                        <div>
                          <div style={{ color: '#374151', fontSize: 9, letterSpacing: 1 }}>TXN ID</div>
                          <div style={{ color: '#6b7280', fontSize: 10, fontFamily: 'monospace', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {txn.transaction_id || '—'}
                          </div>
                        </div>
                        {[
                          ['TO COLLECT',   fmt(txn.amount_to_collect), '#60a5fa'],
                          ['COD CREDIT 1', txn.cod_credit_1 ? fmt(txn.cod_credit_1) : '—', '#34d399'],
                          ['COD CREDIT 2', txn.cod_credit_2 ? fmt(txn.cod_credit_2) : '—', '#34d399'],
                          ['DIFF',         fmt(txn.diff), txn.diff > 0 ? '#f87171' : '#34d399'],
                        ].map(([label, value, color]) => (
                          <div key={label}>
                            <div style={{ color: '#374151', fontSize: 9, letterSpacing: 1 }}>{label}</div>
                            <div style={{ color, fontSize: 11, fontWeight: 700, marginTop: 2 }}>{value}</div>
                          </div>
                        ))}
                      </div>

                      {/* ── Row 3: Prior deposit match/mismatch indicator ── */}
                      {txn.has_prior_deposit && (
                        <div style={{ borderTop: `1px solid ${txn.prior_deposit_match ? '#34d39930' : '#f8717130'}`, paddingTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ color: '#6366f1', fontSize: 9, letterSpacing: 1, fontWeight: 700 }}>📅 PRIOR DEPOSIT</span>
                            <span style={{ color: txn.prior_deposit_match ? '#34d399' : '#f87171', fontSize: 12, fontWeight: 700 }}>{fmt(txn.direct_amt)}</span>
                            <span style={{ color: '#4b5563', fontSize: 9 }}>vs Excel DIFF</span>
                            <span style={{ color: '#9ca3af', fontSize: 11, fontWeight: 700 }}>{fmt(txn.diff)}</span>
                          </div>
                          <span style={{
                            background: txn.prior_deposit_match ? '#0c1c14' : '#3b0f0f',
                            color:      txn.prior_deposit_match ? '#34d399' : '#f87171',
                            borderRadius: 6, padding: '3px 10px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
                          }}>
                            {txn.prior_deposit_match ? '✅ MATCHED' : '⚠️ MISMATCH'}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
      )} {/* end portalTab === 'submit' */}

      {/* ── Hub Summary tab ───────────────────────────────────────────────────── */}
      {portalTab === 'summary' && (
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '28px 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Month filter */}
          {availableMonths.length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ color: '#6b7280', fontSize: 10, letterSpacing: 2, whiteSpace: 'nowrap' }}>MONTH</span>
              {['All', ...availableMonths].map(m => (
                <button key={m} onClick={() => setFilterMonth(m)}
                  style={{ padding: '5px 14px', borderRadius: 20, border: '1px solid', fontSize: 11, fontWeight: 700, letterSpacing: 1, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                    borderColor: filterMonth === m ? '#6366f1' : '#2a2f3e',
                    background:  filterMonth === m ? '#6366f1' : 'transparent',
                    color:       filterMonth === m ? '#fff' : '#6b7280' }}>
                  {m}
                </button>
              ))}
            </div>
          )}

          {/* KPI row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {[
              { label: 'TOTAL DUE', value: fmt(totalDiff),      color: '#60a5fa', bg: '#0c1220' },
              { label: 'SUBMITTED', value: fmt(totalSubmitted),  color: '#34d399', bg: '#0c1c14' },
              { label: 'REMAINING', value: fmt(totalRemaining),  color: totalRemaining > 0 ? '#f87171' : '#34d399', bg: totalRemaining > 0 ? '#1c0f0f' : '#0c1c14' },
            ].map(k => (
              <div key={k.label} style={{ background: k.bg, border: `1px solid ${k.color}30`, borderRadius: 12, padding: '16px 20px' }}>
                <div style={{ color: '#6b7280', fontSize: 10, letterSpacing: 2 }}>{k.label}</div>
                <div style={{ color: k.color, fontSize: 20, fontWeight: 900, marginTop: 6 }}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* Date-wise summary table */}
          {loading ? <Spinner /> : monthFiltered.length === 0 ? (
            <div style={{ background: '#1a1f2e', border: '1px solid #2a2f3e', borderRadius: 12, padding: 32, textAlign: 'center', color: '#4b5563', fontSize: 13 }}>
              No transaction data available{filterMonth !== 'All' ? ` for ${filterMonth}` : ''}.
            </div>
          ) : (
            <div style={{ background: '#0f1117', border: '1px solid #1e2433', borderRadius: 12, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#1a1f2e' }}>
                    {['DATE', 'MONTH', 'TOTAL COLLECTION', 'SUBMITTED', 'SHORTFALL', 'EXCESS', 'STATUS', 'SUBMISSIONS'].map(col => (
                      <th key={col} style={{ color: col === 'EXCESS' ? '#3b82f6' : '#4b5563', fontSize: 10, letterSpacing: 1, padding: '12px 16px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthFiltered.map((txn, i) => {
                    const due       = Number(txn.amount_to_collect || txn.diff) || 0;
                    const submitted = Number(txn.effective_submitted) || 0;
                    const shortfall = Math.max(0, due - submitted);
                    const excess    = Math.max(0, submitted - due);
                    const cleared   = shortfall === 0 && due > 0;
                    const noSub     = submitted === 0;
                    const allSubs   = txn._matchedSubs || [];
                    return (
                      <tr key={txn.id} style={{ borderTop: '1px solid #1e2433', background: i % 2 === 0 ? '#0f1117' : '#0d101a' }}>
                        <td style={{ padding: '11px 16px', color: '#d1d5db', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {txn.collection_date ? formatDate(txn.collection_date) : '—'}
                        </td>
                        <td style={{ padding: '11px 16px', color: '#6b7280', fontSize: 12 }}>{txn.month || '–'}</td>
                        <td style={{ padding: '11px 16px', color: '#60a5fa', fontWeight: 700 }}>{fmt(due)}</td>
                        <td style={{ padding: '11px 16px', color: submitted > 0 ? '#34d399' : '#374151', fontWeight: 700 }}>
                          {submitted > 0 ? fmt(submitted) : '–'}
                        </td>
                        <td style={{ padding: '11px 16px' }}>
                          <span style={{ color: shortfall > 0 ? '#f87171' : '#34d399', fontWeight: 700 }}>
                            {due > 0 ? fmt(shortfall) : '–'}
                          </span>
                        </td>
                        {/* EXCESS */}
                        <td style={{ padding: '11px 16px' }}>
                          {excess > 0
                            ? <span style={{ color: '#3b82f6', fontWeight: 700 }}>{fmt(excess)}</span>
                            : <span style={{ color: '#374151' }}>–</span>}
                        </td>
                        <td style={{ padding: '11px 16px' }}>
                          {due === 0
                            ? <span style={{ color: '#374151', fontSize: 10 }}>N/A</span>
                            : cleared
                              ? <span style={{ background: '#065f46', color: '#34d399', borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>CLEARED</span>
                              : noSub
                                ? <span style={{ background: '#7f1d1d', color: '#f87171', borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>PENDING</span>
                                : <span style={{ background: '#451a03', color: '#fb923c', borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>PARTIAL</span>}
                        </td>
                        <td style={{ padding: '11px 16px' }}>
                          {allSubs.length === 0
                            ? <span style={{ color: '#374151', fontSize: 11 }}>No submissions yet</span>
                            : <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {allSubs.map(s => {
                                  const _rc      = (s.remarks || '').trim().replace(/^\[ADMIN\]\s*/i, '').trim();
                                  const isLoss   = _rc.startsWith('[LOSS]');
                                  const isShort  = _rc.startsWith('[SHORT]');
                                  const isExcess = _rc.startsWith('[EXCESS]');
                                  const typeMapS = { cms: 'CMS', neft_flipkart: 'NEFT-FK', neft_wheeley: 'NEFT-WH' };
                                  const typeLabel = isLoss ? 'LOSS/UTILIZED' : isExcess ? 'EXCESS' : (typeMapS[s.type] || s.type?.toUpperCase() || '—');
                                  const typeColor = isLoss ? '#f97316' : isExcess ? '#3b82f6' : '#60a5fa';
                                  return (
                                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden', paddingBottom: 5, borderBottom: '1px solid #1e243350' }}>
                                      <span title={[typeLabel, fmt(Number(s.amount)), s.submission_date, s.ref_number ? `#${s.ref_number}` : ''].filter(Boolean).join(' · ')}
                                        style={{ color: '#9ca3af', fontSize: 11, flex: 1, minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                        <span style={{ color: typeColor, fontWeight: 700 }}>{typeLabel}</span>
                                        {isShort && <span style={{ color: '#f87171', fontWeight: 700 }}> · SHORT</span>}
                                        {' · '}{fmt(Number(s.amount))}
                                        {' · '}{s.submission_date}
                                        {s.ref_number ? <span style={{ color: '#6b7280' }}> · #{s.ref_number}</span> : ''}
                                      </span>
                                      {/* View Proof */}
                                      {s.slip_url && (
                                        <button onClick={() => setPreviewSlip(s.slip_url)}
                                          style={{ color: '#f59e0b', fontSize: 10, fontWeight: 700, background: '#1c150080', border: '1px solid #f59e0b40', borderRadius: 4, padding: '1px 6px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                          📎 Proof
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )} {/* end portalTab === 'summary' */}

      {/* ── Submission History tab ────────────────────────────────────────────── */}
      {portalTab === 'history' && (
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '28px 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={histMonth} onChange={e => setHistMonth(e.target.value)}
              style={{ background: '#1a1f2e', color: '#f3f4f6', border: '1px solid #2a2f3e', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontFamily: 'inherit' }}>
              {histMonths.map(m => <option key={m}>{m}</option>)}
            </select>
            <select value={histKind} onChange={e => setHistKind(e.target.value)}
              style={{ background: '#1a1f2e', color: '#f3f4f6', border: '1px solid #2a2f3e', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontFamily: 'inherit' }}>
              {['All', 'Full', 'Short', 'Excess', 'Loss'].map(k => <option key={k}>{k}</option>)}
            </select>
            {/* Open dates toggle */}
            <button onClick={() => setHistOpenOnly(v => !v)}
              style={{
                background: histOpenOnly ? '#14532d' : '#1a1f2e',
                color: histOpenOnly ? '#34d399' : '#f59e0b',
                border: `1px solid ${histOpenOnly ? '#34d399' : '#f59e0b'}`,
                borderRadius: 8, padding: '8px 14px', fontSize: 11, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 1,
                whiteSpace: 'nowrap',
              }}>
              {histOpenOnly ? '✓ OPEN DATES' : '○ OPEN DATES'}
            </button>
            {(histMonth !== 'All' || histKind !== 'All' || histOpenOnly) && (
              <button onClick={() => { setHistMonth('All'); setHistKind('All'); setHistOpenOnly(false); }}
                style={{ background: '#1a1f2e', color: '#f87171', border: '1px solid #2a2f3e', borderRadius: 8, padding: '8px 14px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
                ✕ Clear
              </button>
            )}
            <div style={{ marginLeft: 'auto', color: '#4b5563', fontSize: 12 }}>
              {filteredHistSubs.length} entr{filteredHistSubs.length !== 1 ? 'ies' : 'y'}
            </div>
          </div>

          {/* Submissions table */}
          <div style={{ background: '#0f1117', border: '1px solid #1e2433', borderRadius: 12, overflow: 'auto' }}>
            {loading ? <Spinner /> : filteredHistSubs.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#4b5563', fontSize: 13 }}>
                No submissions found{histMonth !== 'All' || histKind !== 'All' || histOpenOnly ? ' matching the filters' : ''}.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#1a1f2e' }}>
                    {['FOR DATE', 'KIND', 'PAYMENT', 'SUBMITTED', 'SHORT AMT', 'SUB DATE', 'REF / UTR', 'REMARKS', 'SLIP'].map(col => (
                      <th key={col} style={{ color: '#4b5563', fontSize: 10, letterSpacing: 1, padding: '12px 16px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredHistSubs.map((s, i) => {
                    const kind = histSubKind(s);
                    const displayRemarks = kind === 'Short'  ? s.remarks?.replace(/^\[SHORT\]\s*/, '')
                      : kind === 'Excess' ? s.remarks?.replace(/^\[EXCESS\]\s*/, '')
                      : kind === 'Loss'   ? s.remarks?.replace(/^\[LOSS\]\s*/, '')
                      : s.remarks;
                    const kindColor = kind === 'Loss'   ? { bg: '#1c0e00', text: '#f97316' }
                      : kind === 'Short'  ? { bg: '#2d0f0f', text: '#f87171' }
                      : kind === 'Excess' ? { bg: '#0c1a2e', text: '#3b82f6' }
                      : { bg: '#0c1c14', text: '#34d399' };
                    const typeMapH = { cms: 'CMS', neft_flipkart: 'NEFT-FK', neft_wheeley: 'NEFT-WH' };
                    const typeLabel = typeMapH[s.type] || s.type?.toUpperCase() || '—';
                    const isNeft = s.type?.includes('neft');
                    const resolvedAmt = s.txn_amount_to_collect ?? null;
                    const shortAmt = resolvedAmt != null ? Math.max(0, Number(resolvedAmt) - Number(s.amount)) : null;
                    return (
                      <tr key={s.id} style={{ borderTop: '1px solid #1e2433', background: s.review_status === 'flagged' ? '#1a0808' : (i % 2 === 0 ? '#0f1117' : '#0d101a') }}>
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          {s.txn_date
                            ? <span style={{ color: '#9ca3af', fontSize: 13 }}>{s.txn_date}</span>
                            : <span style={{ color: '#374151', fontSize: 12 }}>–</span>}
                          {s.is_direct && (
                            <div style={{ color: '#6366f1', fontSize: 9, fontWeight: 700, marginTop: 2 }}>UNTRACKED</div>
                          )}
                          {s.review_status === 'flagged' && (
                            <div style={{ background: '#7f1d1d', color: '#f87171', borderRadius: 4, padding: '1px 7px', fontSize: 9, fontWeight: 700, marginTop: 2, letterSpacing: 1 }}>⚑ SENT BACK</div>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ background: kindColor.bg, color: kindColor.text, borderRadius: 5, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>
                            {kind === 'Loss' ? '💸 LOSS' : kind === 'Short' ? '⚠️ SHORT' : kind === 'Excess' ? '💰 EXCESS' : '✅ FULL'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {kind === 'Loss'
                            ? <span style={{ background: '#1c0e00', color: '#f97316', borderRadius: 5, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>LOSS/UTILIZED</span>
                            : <span style={{ background: isNeft ? '#0c1a2e' : '#1c1a00', color: isNeft ? '#60a5fa' : '#fbbf24', borderRadius: 5, padding: '2px 8px', fontSize: 10, fontWeight: 700, border: `1px solid ${isNeft ? '#1e3a5f' : '#3d3000'}` }}>
                                {typeLabel}
                              </span>}
                        </td>
                        <td style={{ padding: '12px 16px', color: kind === 'Loss' ? '#f97316' : '#34d399', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {fmt(s.amount)}
                        </td>
                        <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                          {shortAmt != null
                            ? <span style={{ color: shortAmt > 0 ? '#f87171' : '#34d399', fontWeight: 700 }}>{shortAmt > 0 ? fmt(shortAmt) : '₹0'}</span>
                            : <span style={{ color: '#374151', fontSize: 12 }}>–</span>}
                        </td>
                        <td style={{ padding: '12px 16px', color: '#9ca3af', fontSize: 13, whiteSpace: 'nowrap' }}>{s.submission_date}</td>
                        <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: 12 }}>{s.ref_number || '–'}</td>
                        <td style={{ padding: '12px 16px', maxWidth: 200 }}>
                          {displayRemarks
                            ? <span style={{ color: '#6b7280', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 180 }}>{displayRemarks}</span>
                            : <span style={{ color: '#374151', fontSize: 12 }}>–</span>}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {s.slip_url
                            ? <button onClick={() => setPreviewSlip(s.slip_url)} style={{ background: 'transparent', border: 'none', color: '#f59e0b', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>View</button>
                            : <span style={{ color: '#374151', fontSize: 12 }}>–</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )} {/* end portalTab === 'history' */}

      </>)} {/* end section === 'cod' */}

      {/* Regular transaction modal */}
      {activeTxn && (
        <SubmissionModal txn={activeTxn} uploadId={uploadMeta?.id} hub={selectedHub}
          preloadedSubs={activeTxn._matchedSubs || []}
          onClose={() => setActiveTxn(null)}
          onSuccess={async () => { setActiveTxn(null); await fetchAll(); }} />
      )}

      {/* Direct (untracked day) submission modal */}
      {activeDirectDate && (
        <SubmissionModal
          txn={{ id: null, remaining: 1, diff: 0, total_submitted: 0, sr_no: null, collection_date: null }}
          uploadId={uploadMeta?.id}
          hub={selectedHub}
          directDate={activeDirectDate}
          onClose={() => setActiveDirectDate(null)}
          onSuccess={async () => { setActiveDirectDate(null); await refreshDirectSubs(); }} />
      )}

      {/* Inline slip/proof preview modal */}
      <SlipPreviewModal url={previewSlip} onClose={() => setPreviewSlip(null)} />
    </div>
  );
}
